import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useStore } from '@tanstack/react-store'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { AlertTriangle, ChartColumn, Check, Cloud, Copy, FileOutput, Loader2, Plus, X } from 'lucide-react'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { MultiSelect } from '@/components/ui/multi-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { AuthService } from '@/services/core/auth'
import { apiErrorDescription, ApiError } from '@/services/core/apiError'
import { AdvertisersApi } from '@/services/advertisers/advertisersApi'
import { OrdersApi } from '@/services/orders/ordersApi'
import { TeamsApi } from '@/services/users/teamsApi'
import {
  availableStatsFields,
  AWS_REGIONS,
  DATA_EXPORT_EVENT_TYPES,
  DATA_EXPORT_SOURCES,
  DATA_EXPORT_TYPES,
  DataExportsApi,
  RAW_LOG_SCHEDULES,
  STATS_FILTER_TYPES,
  STATS_FORMATS,
  type AwsSettings,
  type AzureSettings,
  type DataExport,
  type DataExportFormat,
  type DataExportInput,
  type DataExportSchedule,
  type DataExportSource,
  type DataExportType,
  type GcsSettings,
  type ObjectRef,
  type SheetSettings,
  type StatsFilterType,
} from '@/services/networks/dataExportsApi'
import { formatDate, formatDateTime } from '@/utils/format'
import { AppShell } from '../../components/AppShell'
import { BadgeToggleGroup } from '../../components/BadgeToggleGroup'
import { CopyDataExportDialog } from '../../components/CopyDataExportDialog'
import { CurrencySelect } from '../../components/CurrencySelect'
import { FieldLabel } from '../../components/FieldLabel'
import { FormSkeleton } from '../../components/FormSkeleton'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { ObjectPicker } from '../../components/ObjectPicker'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { StatsBar, StatsBarActivity, StatsBarId } from '../../components/StatsBar'
import { SwitchField } from '../../components/SwitchField'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { useNetwork } from '../../contexts/NetworkContext'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { ObjectStateToggle, useSaveObjectState, type ObjectState } from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'

type FilterChoice = StatsFilterType | 'NONE'

interface DataExportFormValues {
  name: string
  description: string
  source: DataExportSource
  type: DataExportType
  format: DataExportFormat
  schedule: DataExportSchedule
  /** `HH:mm` in the network's time zone, or '' to run at the time it was created. */
  scheduleTime: string
  eventTypes: string[]
  teams: string[]
  aws: AwsSettings
  gcs: GcsSettings
  azure: AzureSettings
  sheet: SheetSettings
  statsFields: string[]
  useLineItemCurrency: boolean
  currency: string
  filterType: FilterChoice
  statsTeams: string[]
  statsAdvertisers: ObjectRef[]
  statsOrders: ObjectRef[]
}

function defaultValues(dataExport: DataExport | null, statsFields: string[]): DataExportFormValues {
  const source: DataExportSource = dataExport?.source === 'RAW_LOG' ? 'RAW_LOG' : 'STATS'
  const type = dataExport?.type ?? 'SHEET'
  return {
    name: dataExport?.name ?? '',
    description: dataExport?.description ?? '',
    source,
    type,
    format: dataExport?.format === 'PARQUET' ? 'PARQUET' : 'CSV',
    schedule: dataExport?.schedule ?? 'DAILY',
    scheduleTime: dataExport?.scheduleTime?.slice(0, 5) ?? '',
    eventTypes: dataExport?.eventTypes ?? [],
    teams: dataExport?.teams.map((team) => team.id) ?? [],
    aws: {
      endpoint: '',
      accessKeyId: '',
      secretKey: '',
      bucketName: '',
      remoteDirectory: '',
      ...dataExport?.awsSettings,
      region: dataExport?.awsSettings.region || (type === 'S3' ? 'EU_CENTRAL_1' : ''),
    },
    gcs: dataExport?.gcsSettings ?? { projectId: '', credentialsJson: '', bucketName: '', remoteDirectory: '' },
    azure: dataExport?.azureSettings ?? {
      servicePrincipalClientId: '',
      servicePrincipalClientSecret: '',
      tenantId: '',
      endpoint: '',
      container: '',
      remoteDirectory: '',
      environment: '',
    },
    sheet: dataExport?.sheetSettings ?? { spreadsheetId: '', sheetTitle: '', credentialsJson: '' },
    statsFields: dataExport ? dataExport.statsSettings.fields : statsFields,
    useLineItemCurrency: dataExport?.statsSettings.useLineItemCurrency ?? false,
    currency: dataExport?.statsSettings.currency ?? '',
    filterType: dataExport?.statsSettings.filterType ?? 'NONE',
    statsTeams: dataExport?.statsSettings.teams.map((team) => team.id) ?? [],
    statsAdvertisers: dataExport?.statsSettings.advertisers ?? [],
    statsOrders: dataExport?.statsSettings.orders ?? [],
  }
}

/** The request body: only the settings for the chosen source and destination. */
function toInput(value: DataExportFormValues, isNew: boolean, objectState: string): DataExportInput {
  const isStats = value.source === 'STATS'
  const { type } = value
  const { endpoint, region, ...aws } = value.aws
  return {
    name: value.name,
    description: value.description,
    source: value.source,
    type,
    ...(isNew ? { data: 'DELTAS' as const, compression: 'GZIP' as const } : {}),
    ...(isStats && type !== 'SHEET' ? { format: value.format } : {}),
    schedule: isStats ? 'DAILY' : value.schedule,
    ...(!isStats
      ? {
          scheduleTime: value.schedule === 'DAILY' && value.scheduleTime ? `${value.scheduleTime}:00` : null,
          eventTypes: value.eventTypes,
          teams: value.teams,
        }
      : {}),
    ...(type === 'S3' ? { awsSettings: { ...aws, region } } : {}),
    ...(type === 'S3_COMPATIBLE_BUCKET' ? { awsSettings: { ...aws, endpoint } } : {}),
    ...(type === 'GCS' ? { gcsSettings: value.gcs } : {}),
    ...(type === 'AZURE_STORAGE_BLOB'
      ? { azureSettings: { ...value.azure, environment: value.azure.environment || 'AZURE' } }
      : {}),
    ...(type === 'SHEET' ? { sheetSettings: value.sheet } : {}),
    ...(isStats
      ? {
          statsSettings: {
            fields: value.statsFields,
            useLineItemCurrency: value.useLineItemCurrency,
            currency: value.useLineItemCurrency ? null : value.currency || null,
            filterType: value.filterType === 'NONE' ? null : value.filterType,
            teams: value.filterType === 'TEAM' ? value.statsTeams : [],
            advertisers: value.filterType === 'ADVERTISER' ? value.statsAdvertisers.map((a) => a.id) : [],
            orders: value.filterType === 'ORDER' ? value.statsOrders.map((o) => o.id) : [],
          },
        }
      : {}),
    objectState,
  }
}

/** A stats export's last run covers up to, but not including, this instant. */
function exportedThrough(lastSuccessfulExport: string): Date | null {
  const end = new Date(lastSuccessfulExport)
  return Number.isNaN(end.getTime()) ? null : new Date(end.getTime() - 1)
}

// ── Inner form component ────────────────────────────────────────────────────

interface DataExportEditFormProps {
  dataExport: DataExport | null
  id: string
  isNew: boolean
}

function DataExportEditForm({ dataExport, id, isNew }: DataExportEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()
  const [copyOpen, setCopyOpen] = useState(false)
  const statsFields = availableStatsFields(!!currentNetwork?.marketplacePlatform)
  const timeZone = currentNetwork?.timeZone ?? 'UTC'

  const form = useForm({
    defaultValues: defaultValues(dataExport, statsFields),
    onSubmit: ({ value }) =>
      mutation.mutate(toInput(value, isNew, (dataExport?.objectState ?? 'ACTIVE') as ObjectState)),
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => DataExportsApi.update(id, { name: dataExport?.name ?? '', objectState }),
    ['data-export', id],
    ['data-exports']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)
  const values = useStore(form.store, (s) => s.values)
  const isStats = values.source === 'STATS'
  const set = form.setFieldValue

  const needsTeams = !isStats || values.filterType === 'TEAM'
  const { data: teams = [], isLoading: teamsLoading } = useQuery({
    queryKey: ['teams', ''],
    queryFn: () => TeamsApi.getAll(),
    enabled: needsTeams,
    staleTime: 5 * 60 * 1000,
  })
  const teamOptions = teams.map((team) => ({ value: team.id, label: team.name }))

  const mutation = useMutation({
    mutationFn: (input: DataExportInput) =>
      isNew ? DataExportsApi.create(id, input) : DataExportsApi.update(id, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['data-export', id], data)
      queryClient.invalidateQueries({ queryKey: ['data-exports'] })
      markClean()
      if (isNew) navigate(`/admin/data-exports/${id}`, { replace: true })
      toast.success(t('common.saved'), {
        description: isNew ? t('dataExports.createdSuccess') : t('dataExports.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError
          ? error.message
          : isNew
            ? t('dataExports.createFailed')
            : t('dataExports.saveFailed')
      toast.error(summary, { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  const changeSource = (source: DataExportSource) => {
    set('source', source)
    const type = DATA_EXPORT_TYPES[source].includes(values.type) ? values.type : DATA_EXPORT_TYPES[source][0]
    changeType(type)
    if (source === 'STATS') set('schedule', 'DAILY')
  }

  const changeType = (type: DataExportType) => {
    set('type', type)
    if (type === 'S3' && !values.aws.region) set('aws.region', 'EU_CENTRAL_1')
  }

  const changeFilterType = (filterType: FilterChoice) => {
    set('filterType', filterType)
    set('statsTeams', [])
    set('statsAdvertisers', [])
    set('statsOrders', [])
  }

  const crumbs = [
    { label: t('dataExports.title'), href: '/admin/data-exports' },
    { label: isNew ? t('dataExports.newDataExport') : (dataExport?.name ?? t('dataExports.editTitle')) },
  ]

  // ── Field renderers ──

  const textField = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    options: { secret?: boolean; full?: boolean; tooltip?: string; placeholder?: string } = {}
  ) => (
    <div className={`p-field${options.full ? ' full' : ''}`}>
      <FieldLabel tooltip={options.tooltip}>{label}</FieldLabel>
      <Input
        type={options.secret ? 'password' : 'text'}
        autoComplete={options.secret ? 'new-password' : 'off'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={options.placeholder}
      />
    </div>
  )

  const credentialsField = (value: string, onChange: (v: string) => void) => (
    <div className="p-field full">
      <FieldLabel tooltip={t('dataExports.secretHelp')}>{t('dataExports.credentialsJson')}</FieldLabel>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        className="font-mono text-xs"
        placeholder={'{ "type": "service_account", … }'}
      />
    </div>
  )

  const enumSelect = <T extends string>(
    label: string,
    value: T,
    options: readonly T[],
    onChange: (v: T) => void,
    optionLabel: (v: T) => string,
    tooltip?: string
  ) => (
    <div className="p-field">
      <FieldLabel tooltip={tooltip}>{label}</FieldLabel>
      <Select label={label} value={value} onValueChange={(v) => onChange(v as T)}>
        <SelectTrigger>
          <SelectValue>{optionLabel(value)}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((v) => (
            <SelectItem key={v} value={v}>
              {optionLabel(v)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )

  const destinationFields = () => {
    switch (values.type) {
      case 'S3':
      case 'S3_COMPATIBLE_BUCKET':
        return (
          <>
            {values.type === 'S3_COMPATIBLE_BUCKET' &&
              textField(t('dataExports.bucketEndpoint'), values.aws.endpoint, (v) => set('aws.endpoint', v), {
                full: true,
                placeholder: 'https://',
              })}
            {textField(t('dataExports.accessKeyId'), values.aws.accessKeyId, (v) => set('aws.accessKeyId', v))}
            {textField(t('dataExports.secretKey'), values.aws.secretKey, (v) => set('aws.secretKey', v), {
              secret: true,
              tooltip: t('dataExports.secretHelp'),
            })}
            {values.type === 'S3' &&
              enumSelect(
                t('dataExports.awsRegion'),
                values.aws.region,
                AWS_REGIONS,
                (v) => set('aws.region', v),
                (v) => v
              )}
            {textField(t('dataExports.bucketName'), values.aws.bucketName, (v) => set('aws.bucketName', v))}
            {textField(t('dataExports.directory'), values.aws.remoteDirectory, (v) =>
              set('aws.remoteDirectory', v)
            )}
          </>
        )
      case 'GCS':
        return (
          <>
            {textField(t('dataExports.projectId'), values.gcs.projectId, (v) => set('gcs.projectId', v))}
            {textField(t('dataExports.bucketName'), values.gcs.bucketName, (v) => set('gcs.bucketName', v))}
            {textField(t('dataExports.directory'), values.gcs.remoteDirectory, (v) =>
              set('gcs.remoteDirectory', v)
            )}
            {credentialsField(values.gcs.credentialsJson, (v) => set('gcs.credentialsJson', v))}
          </>
        )
      case 'AZURE_STORAGE_BLOB':
        return (
          <>
            {textField(t('dataExports.clientId'), values.azure.servicePrincipalClientId, (v) =>
              set('azure.servicePrincipalClientId', v)
            )}
            {textField(
              t('dataExports.clientSecret'),
              values.azure.servicePrincipalClientSecret,
              (v) => set('azure.servicePrincipalClientSecret', v),
              { secret: true, tooltip: t('dataExports.secretHelp') }
            )}
            {textField(t('dataExports.tenantId'), values.azure.tenantId, (v) => set('azure.tenantId', v))}
            {textField(t('dataExports.endpoint'), values.azure.endpoint, (v) => set('azure.endpoint', v))}
            {textField(t('dataExports.container'), values.azure.container, (v) => set('azure.container', v))}
            {textField(t('dataExports.directory'), values.azure.remoteDirectory, (v) =>
              set('azure.remoteDirectory', v)
            )}
          </>
        )
      case 'SHEET':
        return (
          <>
            {textField(t('dataExports.spreadsheetId'), values.sheet.spreadsheetId, (v) =>
              set('sheet.spreadsheetId', v)
            )}
            {textField(t('dataExports.sheetTitle'), values.sheet.sheetTitle, (v) => set('sheet.sheetTitle', v), {
              tooltip: t('dataExports.sheetTitleHelp'),
            })}
            {credentialsField(values.sheet.credentialsJson, (v) => set('sheet.credentialsJson', v))}
          </>
        )
      default:
        return null
    }
  }

  const lastExport = dataExport?.lastSuccessfulExport
    ? dataExport.source === 'STATS'
      ? formatDate(exportedThrough(dataExport.lastSuccessfulExport) ?? dataExport.lastSuccessfulExport)
      : formatDateTime(dataExport.lastSuccessfulExport)
    : null

  return (
    <AppShell crumbs={crumbs} showBack>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={dataExport?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.dataExport')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('dataExports.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {!isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <Button type="button" variant="outline" onClick={() => setCopyOpen(true)}>
                <Copy className="size-3.5" />
                {t('copyDataExport.copy')}
              </Button>
              <ObjectStateToggle value={dataExport!.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {dataExport && !isNew && (
          <StatsBar
            items={[
              { label: t('common.id'), content: <StatsBarId value={dataExport.id} /> },
              ...(lastExport
                ? [
                    {
                      label: dataExport.source === 'STATS' ? t('dataExports.exportedThrough') : t('dataExports.lastExport'),
                      content: lastExport,
                    },
                  ]
                : []),
              ...(dataExport.nextScheduledFor
                ? [{ label: t('dataExports.nextExport'), content: formatDateTime(dataExport.nextScheduledFor) }]
                : []),
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(dataExport.createTime)}
                    updated={formatDate(dataExport.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <div className="flex flex-col gap-3.5">
          <Card>
            <CardHeader>
              <CardTitle>
                <CardIcon icon={FileOutput} />
                {t('dataExports.details')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="field-grid">
                {textField(t('common.description'), values.description, (v) => set('description', v), {
                  full: true,
                  placeholder: t('dataExports.descriptionPlaceholder'),
                })}
                {enumSelect(
                  t('dataExports.source'),
                  values.source,
                  DATA_EXPORT_SOURCES,
                  changeSource,
                  (v) => t(`dataExports.sources.${v}`),
                  t('dataExports.sourceHelp')
                )}
                {isStats ? (
                  <div className="p-field">
                    <FieldLabel hint={timeZone}>{t('dataExports.schedule')}</FieldLabel>
                    <p className="text-sm text-(--ad-text-muted)">{t('dataExports.statsSchedule')}</p>
                  </div>
                ) : (
                  <>
                    {enumSelect(
                      t('dataExports.schedule'),
                      values.schedule,
                      RAW_LOG_SCHEDULES,
                      (v) => set('schedule', v),
                      (v) => t(`dataExports.schedules.${v}`)
                    )}
                    {values.schedule === 'DAILY' && (
                      <div className="p-field">
                        <FieldLabel tooltip={t('dataExports.scheduleTimeHelp')} hint={timeZone}>
                          {t('dataExports.scheduleTime')}
                        </FieldLabel>
                        <Input
                          type="time"
                          value={values.scheduleTime}
                          onChange={(e) => set('scheduleTime', e.target.value)}
                        />
                      </div>
                    )}
                    <div className="p-field full">
                      <FieldLabel tooltip={t('dataExports.eventTypesHelp')}>
                        {t('dataExports.eventTypes')}
                      </FieldLabel>
                      <BadgeToggleGroup
                        options={DATA_EXPORT_EVENT_TYPES.map((v) => ({
                          value: v,
                          label: t(`dataExports.eventTypeNames.${v}`),
                        }))}
                        value={values.eventTypes}
                        onChange={(v) => set('eventTypes', v)}
                      />
                    </div>
                    <div className="p-field full">
                      <FieldLabel tooltip={t('dataExports.teamsHelp')}>{t('dataExports.teams')}</FieldLabel>
                      <MultiSelect
                        options={teamOptions}
                        value={values.teams}
                        onChange={(v) => set('teams', v)}
                        placeholder={teamsLoading ? t('common.loading') : t('dataExports.selectTeams')}
                      />
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                <CardIcon icon={Cloud} />
                {t('dataExports.destination')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="field-grid">
                {enumSelect(
                  t('dataExports.destinationType'),
                  values.type,
                  DATA_EXPORT_TYPES[values.source],
                  changeType,
                  (v) => t(`dataExports.types.${v}`)
                )}
                {isStats && values.type !== 'SHEET'
                  ? enumSelect(
                      t('dataExports.format'),
                      values.format,
                      STATS_FORMATS,
                      (v) => set('format', v),
                      (v) => v
                    )
                  : null}
                {destinationFields()}
              </div>
            </CardContent>
          </Card>

          {isStats && (
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={ChartColumn} />
                  {t('dataExports.statsSettings')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  {values.type === 'SHEET' && (
                    <div className="p-field full">
                      <Alert variant="warning">
                        <AlertTriangle />
                        <AlertDescription>{t('dataExports.sheetCapacityWarning')}</AlertDescription>
                      </Alert>
                    </div>
                  )}
                  <div className="p-field full">
                    <div className="flex items-center justify-between gap-2">
                      <FieldLabel>
                        {t('dataExports.fields', { count: values.statsFields.length })}
                      </FieldLabel>
                      <div className="flex gap-1">
                        <Button type="button" variant="ghost" size="sm" onClick={() => set('statsFields', statsFields)}>
                          {t('dataExports.selectAll')}
                        </Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => set('statsFields', [])}>
                          {t('dataExports.selectNone')}
                        </Button>
                      </div>
                    </div>
                    <BadgeToggleGroup
                      options={statsFields.map((v) => ({ value: v, label: v.replace(/_/g, ' ') }))}
                      // Kept in the order the export writes them, not the order clicked.
                      value={values.statsFields}
                      onChange={(v) => set('statsFields', statsFields.filter((field) => v.includes(field)))}
                    />
                  </div>
                  <div className="p-field full">
                    <SwitchField
                      label={t('dataExports.useLineItemCurrency')}
                      description={t('dataExports.useLineItemCurrencyHelp')}
                      checked={values.useLineItemCurrency}
                      onCheckedChange={(v) => {
                        set('useLineItemCurrency', v)
                        set('currency', '')
                      }}
                    />
                  </div>
                  {!values.useLineItemCurrency && (
                    <div className="p-field">
                      <FieldLabel>{t('common.currency')}</FieldLabel>
                      <CurrencySelect
                        value={values.currency}
                        onValueChange={(v) => set('currency', v)}
                        fallback={currentNetwork?.defaultCurrency}
                      />
                    </div>
                  )}
                  {enumSelect<FilterChoice>(
                    t('dataExports.filterType'),
                    values.filterType,
                    ['NONE', ...STATS_FILTER_TYPES],
                    changeFilterType,
                    (v) => t(`dataExports.filterTypes.${v}`)
                  )}
                  {values.filterType === 'TEAM' && (
                    <div className="p-field full">
                      <FieldLabel>{t('dataExports.teams')}</FieldLabel>
                      <MultiSelect
                        options={teamOptions}
                        value={values.statsTeams}
                        onChange={(v) => set('statsTeams', v)}
                        placeholder={teamsLoading ? t('common.loading') : t('dataExports.selectTeams')}
                      />
                    </div>
                  )}
                  {values.filterType === 'ADVERTISER' && (
                    <div className="p-field full">
                      <FieldLabel>{t('dataExports.advertisers')}</FieldLabel>
                      <ObjectPicker
                        queryKey="data-export-advertiser-picker"
                        search={async (term) =>
                          (await AdvertisersApi.getPage(1, 50, false, term)).results
                        }
                        selected={values.statsAdvertisers}
                        onChange={(v) => set('statsAdvertisers', v.map(({ id, name }) => ({ id, name: name ?? '' })))}
                        addLabel={t('dataExports.addAdvertisers')}
                        emptyText={t('dataExports.noAdvertisers')}
                      />
                    </div>
                  )}
                  {values.filterType === 'ORDER' && (
                    <div className="p-field full">
                      <FieldLabel>{t('dataExports.orders')}</FieldLabel>
                      <ObjectPicker
                        queryKey="data-export-order-picker"
                        search={async (term) => (await OrdersApi.getPage(1, 50, false, term)).results}
                        selected={values.statsOrders}
                        onChange={(v) => set('statsOrders', v.map(({ id, name }) => ({ id, name: name ?? '' })))}
                        addLabel={t('dataExports.addOrders')}
                        emptyText={t('dataExports.noOrders')}
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div
          className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}
        >
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/admin/data-exports')}
              disabled={mutation.isPending}
            >
              <X className="size-4" />
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('dataExports.newDataExport') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      {dataExport && copyOpen && (
        <CopyDataExportDialog open={copyOpen} onOpenChange={setCopyOpen} dataExport={dataExport} />
      )}
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function DataExportEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const {
    data: dataExport,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['data-export', id],
    queryFn: () => DataExportsApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbsLoading = [
    { label: t('dataExports.title'), href: '/admin/data-exports' },
    { label: isNew ? t('dataExports.newDataExport') : t('dataExports.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={4} columns={2} />
      </AppShell>
    )
  }

  // Remount after a save so the form starts from what the server returned.
  return (
    <DataExportEditForm
      key={`${id}:${dataExport?.updateTime ?? ''}`}
      dataExport={dataExport ?? null}
      id={id!}
      isNew={isNew}
    />
  )
}
